import { useMemo } from 'react';
import { Navbar } from 'react-daisyui';
import { Link } from 'react-router-dom';
import { userService } from '../../reducers/permissions/userSlice';
import { workspaceService } from '../../reducers/workspaces/workspaceSlice';
import { useAppDispatch, useTypedSelector } from '../../stateStore';
import { isInternalUser } from '../../utils/helpers/common';
import IconButton from '../IconButton';
import UserProfileMini from '../auth/UserProfileMini';
import AppHeadingWithBreadcrumbs from './AppHeadingWithBreadcrumbs';
import NotificationIcon from './NotificationIcon';
import { NotificationsNavGate } from './NotificationsNavGate';

export interface IAppHeaderV2Props {
  hasBreadcrumbs?: boolean;
  hasLogo?: boolean;
  hasMenuToggle?: boolean;
  hasUserProfile?: boolean;
  initialCrumb?: string;
  onMenuToggle?: () => void;
}

const AppHeaderV2 = ({
  hasBreadcrumbs,
  hasLogo = true,
  hasMenuToggle = true,
  hasUserProfile = true,
  initialCrumb,
  onMenuToggle,
}: IAppHeaderV2Props) => {
  const dispatch = useAppDispatch();

  const workspace = useTypedSelector(workspaceService.selectors.workspace);
  const userMe = useTypedSelector(userService.selectors.userMe);

  const isAuthenticated = useMemo(() => userMe, [userMe]);

  const handleResetWorkspace = () => {
    dispatch(workspaceService.actions.resetSingleWorkspace());
  };

  const manageWorkspacesBreadcrumbs = window.location.href.includes(
    'manage-workspaces',
  )
    ? true
    : hasBreadcrumbs;

  return (
    <Navbar
      id="nav-header"
      className="flex h-[70px] items-center justify-between border-l border-[#565656] bg-sr-black px-5 py-2.5 lg:px-8"
    >
      <Navbar.Start className="gap-2">
        {hasMenuToggle && (
          <IconButton
            label="Menu"
            className="text-white lg:hidden"
            icon="list"
            iconClassName="w-[18px] h-[18px]"
            iconSize="6"
            tipPosition="right"
            onClick={() => onMenuToggle?.()}
          />
        )}
        <AppHeadingWithBreadcrumbs
          hasBreadcrumbs={manageWorkspacesBreadcrumbs}
          initialCrumb={initialCrumb}
          isInternal={isInternalUser(userMe)}
          workspaceName={workspace?.workspaceName}
          onResetWorkspace={handleResetWorkspace}
        />
      </Navbar.Start>
      {hasLogo && (
        <Navbar.Center>
          <Link to="/dashboard">
            <img
              alt="Screenrights"
              className="h-8"
              src="/screenrights-logo.svg"
            />
          </Link>
        </Navbar.Center>
      )}
      {isAuthenticated && hasUserProfile && (
        <Navbar.End className="gap-1.5">
          {/* The gate sits outside the sizing box: inside it, a hidden bell
              still reserves 48px plus the parent gap and pushes the avatar
              off-centre for everyone while the flag is off. */}
          <NotificationsNavGate>
            <div className="flex h-12 w-12 items-center justify-center gap-2.5">
              <NotificationIcon />
            </div>
          </NotificationsNavGate>

          <div className="flex items-center gap-1.5 py-1.5">
            <UserProfileMini />
          </div>
        </Navbar.End>
      )}
    </Navbar>
  );
};

export default AppHeaderV2;
